import type { DefaultResponseBody } from "@gerama/types";
import type { ApiClient } from "../client";

export type PlayableItemType = "song" | "video";
export type InteractionItemType = "song" | "video" | "album" | "playlist" | "artist";

/** Mirrors `LyricsResponse` — `content` is LRC when `isSynced`, else plain text. */
export interface Lyrics {
  id: number;
  songId: number;
  format: string;
  isSynced: boolean;
  content: string;
}

/** Mirrors `ListeningProgressResponse`. */
export interface ListeningProgress {
  itemType: PlayableItemType;
  itemId: number;
  positionSeconds: number;
  durationSeconds: number;
  completed: boolean;
  updatedAt: string;
}

/**
 * What the players need beyond the catalogue: likes, lyrics, resume points
 * and play reporting (`/interaction`, `/songs/:id/lyrics`, `/listening`,
 * `/streaming`). Reads that need a user (`status`, `progress`) must only be
 * called with a token.
 */
export function createCorePlaybackApi(client: ApiClient) {
  return {
    likeStatus: (itemType: InteractionItemType, itemId: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<{ liked: boolean; saved: boolean }>>("/interaction/status", {
          query: { itemType, itemId },
          signal,
        })
        .then((res) => res.data ?? { liked: false, saved: false }),

    /** Liked/saved state of many items of one type in one call (a list page's hearts) — up to 200 ids. */
    statusBatch: (itemType: InteractionItemType, ids: number[], signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<{ liked: number[]; saved: number[] }>>(
          "/interaction/status/batch",
          {
            query: { itemType, ids: ids.join(",") },
            signal,
          },
        )
        .then((res) => res.data ?? { liked: [], saved: [] }),

    toggleLike: (itemType: InteractionItemType, itemId: number) =>
      client
        .request<DefaultResponseBody<{ liked: boolean }>>("/interaction/likes", {
          method: "POST",
          body: { itemType, itemId },
        })
        .then((res) => res.data?.liked ?? false),

    /** Save / unsave to My Music — returns the new saved state. */
    toggleSaved: (itemType: InteractionItemType, itemId: number) =>
      client
        .request<DefaultResponseBody<{ saved: boolean }>>("/interaction/saved", {
          method: "POST",
          body: { itemType, itemId },
        })
        .then((res) => res.data?.saved ?? false),

    /** Follower count for anyone; `following` is filled in when a token is sent. */
    artistFollowStatus: (artistId: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<{ followerCount: number; following: boolean | null }>>(
          `/interaction/artists/${artistId}/followers`,
          { signal },
        )
        .then((res) => res.data ?? { followerCount: 0, following: null }),

    followArtist: (artistId: number) =>
      client
        .request<DefaultResponseBody<{ followerCount: number; following: boolean }>>(
          `/interaction/artists/${artistId}/follow`,
          { method: "POST" },
        )
        .then((res) => res.data),

    unfollowArtist: (artistId: number) =>
      client
        .request<DefaultResponseBody<{ followerCount: number; following: boolean }>>(
          `/interaction/artists/${artistId}/follow`,
          { method: "DELETE" },
        )
        .then((res) => res.data),

    /** "Suggest less": hides the artist from recommendations (never from search). */
    setSuggestLess: (artistId: number, on: boolean) =>
      client
        .request<DefaultResponseBody<{ suggestLess: boolean }>>(
          `/interaction/artists/${artistId}/suggest-less`,
          { method: on ? "POST" : "DELETE" },
        )
        .then((res) => res.data?.suggestLess ?? on),

    /** `null` when the song has no lyrics (the endpoint answers with an error status). */
    lyrics: (songId: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<Lyrics>>(`/songs/${songId}/lyrics`, { signal })
        .then((res) => res.data ?? null)
        .catch(() => null),

    progress: (itemType: PlayableItemType, itemId: number, signal?: AbortSignal) =>
      client
        .request<DefaultResponseBody<ListeningProgress>>(`/listening/me/${itemType}/${itemId}`, {
          signal,
        })
        .then((res) => res.data ?? null)
        .catch(() => null),

    saveProgress: (body: {
      itemType: PlayableItemType;
      itemId: number;
      positionSeconds: number;
      durationSeconds?: number;
      completed?: boolean;
    }) =>
      client.request<DefaultResponseBody<null>>("/listening/progress", {
        method: "PUT",
        body: {
          ...body,
          positionSeconds: Math.floor(body.positionSeconds),
          durationSeconds: body.durationSeconds ? Math.floor(body.durationSeconds) : undefined,
        },
      }),

    startSession: (userAgent?: string) =>
      client
        .request<DefaultResponseBody<{ id: number }>>("/streaming/start", {
          method: "POST",
          body: { userAgent },
        })
        .then((res) => res.data?.id ?? null),

    endSession: (sessionId: number) =>
      client.request<DefaultResponseBody<unknown>>("/streaming/end", {
        method: "POST",
        body: { sessionId },
      }),

    logPlay: (itemType: PlayableItemType, itemId: number) =>
      client.request<DefaultResponseBody<null>>("/streaming/play-log", {
        method: "POST",
        body: { itemType, itemId },
      }),
  };
}
